// apps/booking-frontend/src/pages/admin/ManualPage.tsx
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ManualPage: React.FC = () => {
  const { t } = useTranslation(['staticContent', 'pages', 'translation']);
  const location = useLocation();
  const [initialScrollTarget, setInitialScrollTarget] = useState<string | null>(null);

  // 安全的陣列翻譯取得函數，防止 t(...).map is not a function 錯誤
  const getSafeArray = (key: string): any[] => {
    const result = t(key, { returnObjects: true });
    return Array.isArray(result) ? result : [];
  };

  const sections = [
    { key: 'gettingStarted', title: t('staticContent:manual.sections.gettingStarted.title') },
    { key: 'dashboard', title: t('staticContent:manual.sections.dashboard.title') },
    { key: 'bookings', title: t('staticContent:manual.sections.bookings.title') },
    { key: 'calendar', title: t('staticContent:manual.sections.calendar.title') },
    { key: 'customers', title: t('staticContent:manual.sections.customers.title') },
    { key: 'services', title: t('staticContent:manual.sections.services.title') },
    { key: 'staff', title: t('staticContent:manual.sections.staff.title') },
    { key: 'bookingLink', title: t('staticContent:manual.sections.bookingLink.title') },
    { key: 'settings', title: t('staticContent:manual.sections.settings.title') },
    { key: 'dataManagement', title: t('staticContent:manual.sections.dataManagement.title') }
  ];

  // 導覽系統對應的章節映射
  const navigationToSectionMap: Record<string, string> = {
    '/admin': 'dashboard',
    '/admin/bookings': 'bookings',
    '/admin/calendar': 'calendar',
    '/admin/customers': 'customers',
    '/admin/services': 'services',
    '/admin/staff': 'staff',
    '/admin/booking-link': 'bookingLink',
    '/admin/settings': 'settings',
    '/admin/data-io': 'dataManagement'
  };

  // 在頁面載入時判斷初始滾動目標
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const fromSystem = urlParams.get('from');
    let targetSection = '';

    // 優先級1: URL 查詢參數
    if (fromSystem && navigationToSectionMap[fromSystem]) {
      targetSection = navigationToSectionMap[fromSystem];
    }
    else {
      // 優先級2: sessionStorage 中記錄的最後活動頁面
      const lastActivePage = sessionStorage.getItem('lastActivePage');

      // 特殊處理：如果 lastActivePage 是儀表板，跳到頂部
      if (lastActivePage === '/admin') {
        targetSection = '';
      }
      else if (lastActivePage && navigationToSectionMap[lastActivePage]) {
        targetSection = navigationToSectionMap[lastActivePage];
      }

      // 優先級3: 備用方案檢查 referrer
      if (!targetSection) {
        try {
          const referrer = document.referrer;

          if (referrer) {
            const referrerUrl = new URL(referrer);
            const referrerPath = referrerUrl.pathname;

            if (navigationToSectionMap[referrerPath]) {
              targetSection = navigationToSectionMap[referrerPath];
            }
          }
        } catch (error) {
          // URL 解析失敗時忽略
        }
      }

      // 優先級4: URL hash
      if (!targetSection && location.hash) {
        const hashTarget = location.hash.substring(1);
        // 檢查是否為有效章節
        if (sections.find(section => section.key === hashTarget)) {
          targetSection = hashTarget;
        }
      }
    }

    // 設定初始滾動目標
    setInitialScrollTarget(targetSection || null);
  }, [location.search, location.hash]);

  // 在頁面內容載入後執行初始滾動
  useEffect(() => {
    if (initialScrollTarget) {
      // 立即執行初始滾動，不等待狀態變更
      const executeScroll = () => {
        const element = document.getElementById(initialScrollTarget);
        if (element) {
          scrollToSection(initialScrollTarget);
        } else {
          // 如果元素還沒渲染，稍等片刻再試
          setTimeout(executeScroll, 10);
        }
      };

      // 使用 requestAnimationFrame 確保在下一個渲染循環執行
      requestAnimationFrame(executeScroll);
    }
  }, [initialScrollTarget]);

  const scrollToSection = (sectionKey: string) => {
    const element = document.getElementById(sectionKey);
    if (element) {
      const elementPosition = element.offsetTop;
      const offsetPosition = elementPosition - 30; // 偏移，確保標題不被遮住

      window.scrollTo({
        top: offsetPosition,
        behavior: 'auto'
      });
    }
  };

  return (
    <div className="container mx-auto px-4 md:px-10 py-6 pb-32">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
          {t('staticContent:manual.title')}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
          {/* 側邊目錄 */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <nav className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
                <h2 className="font-semibold text-gray-900 dark:text-white mb-4">
                  {t('staticContent:manual.tableOfContents')}
                </h2>
                <ul className="space-y-2">
                  {sections.map((section) => (
                    <li key={section.key}>
                      <button
                        onClick={() => scrollToSection(section.key)}
                        className="text-left w-full text-brand-600 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-300 hover:underline"
                      >
                        {section.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>

          {/* 主要內容 */}
          <div className="lg:col-span-5">

              {/* 快速入門 */}
              <section id="gettingStarted" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {t('staticContent:manual.sections.gettingStarted.title')}
                </h2>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
                    {t('staticContent:manual.sections.gettingStarted.welcome.title')}
                  </h3>
                  <p className="text-blue-800 dark:text-blue-200">
                    {t('staticContent:manual.sections.gettingStarted.welcome.description')}
                  </p>
                </div>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                  {t('staticContent:manual.sections.gettingStarted.features.title')}
                </h4>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
                  {getSafeArray('staticContent:manual.sections.gettingStarted.features.items').map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>

              {/* 儀表板 */}
              <section id="dashboard" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {t('staticContent:manual.sections.dashboard.title')}
                </h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('staticContent:manual.sections.dashboard.description')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">
                      {t('staticContent:manual.sections.dashboard.cards.statistics.title')}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('staticContent:manual.sections.dashboard.cards.statistics.description')}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">
                      {t('staticContent:manual.sections.dashboard.cards.upcoming.title')}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('staticContent:manual.sections.dashboard.cards.upcoming.description')}
                    </p>
                  </div>
                </div>
              </section>

              {/* 預約管理 */}
              <section id="bookings" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {t('staticContent:manual.sections.bookings.title')}
                </h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('staticContent:manual.sections.bookings.description')}
                </p>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                  {t('staticContent:manual.sections.bookings.features.title')}
                </h4>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
                  {getSafeArray('staticContent:manual.sections.bookings.features.items').map((item: any, index: number) => (
                    <li key={index}>
                      <strong>{item.title}</strong>{item.description}
                    </li>
                  ))}
                </ul>
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">
                    {t('staticContent:manual.sections.bookings.tip.title')}
                  </h4>
                  <p className="text-green-800 dark:text-green-200 text-sm">
                    {t('staticContent:manual.sections.bookings.tip.content')}
                  </p>
                </div>
              </section>

              {/* 日曆視圖 */}
              <section id="calendar" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {t('staticContent:manual.sections.calendar.title')}
                </h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('staticContent:manual.sections.calendar.description')}
                </p>

                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                  {t('staticContent:manual.sections.calendar.viewModes.title')}
                </h4>
                <div className="space-y-4 mb-6">
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h5 className="font-semibold mb-2 text-brand-700 dark:text-brand-400">
                      {t('staticContent:manual.sections.calendar.viewModes.day.title')}
                    </h5>
                    <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      {getSafeArray('staticContent:manual.sections.calendar.viewModes.day.features').map((feature: string, index: number) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h5 className="font-semibold mb-2 text-green-700 dark:text-green-400">
                      {t('staticContent:manual.sections.calendar.viewModes.week.title')}
                    </h5>
                    <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      {getSafeArray('staticContent:manual.sections.calendar.viewModes.week.features').map((feature: string, index: number) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h5 className="font-semibold mb-2 text-blue-700 dark:text-blue-400">
                      {t('staticContent:manual.sections.calendar.viewModes.staff.title')}
                    </h5>
                    <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      {getSafeArray('staticContent:manual.sections.calendar.viewModes.staff.features').map((feature: string, index: number) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                  {t('staticContent:manual.sections.calendar.commonFeatures.title')}
                </h4>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
                  {getSafeArray('staticContent:manual.sections.calendar.commonFeatures.items').map((item: any, index: number) => (
                    <li key={index}>
                      <strong>{item.title}</strong>{item.description}
                    </li>
                  ))}
                </ul>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-2">
                    {t('staticContent:manual.sections.calendar.warning.title')}
                  </h4>
                  <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                    {t('staticContent:manual.sections.calendar.warning.content')}
                  </p>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">
                    {t('staticContent:manual.sections.calendar.tip.title')}
                  </h4>
                  <ul className="space-y-1 text-green-800 dark:text-green-200 text-sm">
                    {getSafeArray('staticContent:manual.sections.calendar.tip.items').map((item: string, index: number) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>
              </section>

              {/* 客戶管理 */}
              <section id="customers" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {t('staticContent:manual.sections.customers.title')}
                </h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('staticContent:manual.sections.customers.description')}
                </p>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                  {t('staticContent:manual.sections.customers.features.title')}
                </h4>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
                  {getSafeArray('staticContent:manual.sections.customers.features.items').map((item: any, index: number) => (
                    <li key={index}>
                      <strong>{item.title}</strong>{item.description}
                    </li>
                  ))}
                </ul>
              </section>

              {/* 服務項目 */}
              <section id="services" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {t('staticContent:manual.sections.services.title')}
                </h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('staticContent:manual.sections.services.description')}
                </p>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                  {t('staticContent:manual.sections.services.features.title')}
                </h4>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
                  {getSafeArray('staticContent:manual.sections.services.features.items').map((item: any, index: number) => (
                    <li key={index}>
                      <strong>{item.title}</strong>{item.description}
                    </li>
                  ))}
                </ul>
                <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 mb-6">
                  <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                    {t('staticContent:manual.sections.services.recommendations.title')}
                  </h4>
                  <div className="space-y-3">
                    <div className="border-l-4 border-brand-500 pl-4">
                      <p className="font-semibold text-brand-700 dark:text-brand-400">
                        {t('staticContent:manual.sections.services.recommendations.duration.title')}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('staticContent:manual.sections.services.recommendations.duration.description')}
                      </p>
                    </div>
                    <div className="border-l-4 border-green-500 pl-4">
                      <p className="font-semibold text-green-700 dark:text-green-400">
                        {t('staticContent:manual.sections.services.recommendations.pricing.title')}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('staticContent:manual.sections.services.recommendations.pricing.description')}
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* 員工管理 */}
              <section id="staff" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {t('staticContent:manual.sections.staff.title')}
                </h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('staticContent:manual.sections.staff.description')}
                </p>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                  {t('staticContent:manual.sections.staff.features.title')}
                </h4>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
                  {getSafeArray('staticContent:manual.sections.staff.features.items').map((item: any, index: number) => (
                    <li key={index}>
                      <strong>{item.title}</strong>{item.description}
                    </li>
                  ))}
                </ul>
              </section>

              {/* 訂位連結 */}
              <section id="bookingLink" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {t('staticContent:manual.sections.bookingLink.title')}
                </h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('staticContent:manual.sections.bookingLink.description')}
                </p>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                  {t('staticContent:manual.sections.bookingLink.features.title')}
                </h4>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
                  {getSafeArray('staticContent:manual.sections.bookingLink.features.items').map((item: any, index: number) => (
                    <li key={index}>
                      <strong>{item.title}</strong>{item.description}
                    </li>
                  ))}
                </ul>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                    {t('staticContent:manual.sections.bookingLink.tip.title')}
                  </h4>
                  <ul className="text-sm text-blue-800 dark:text-blue-400 space-y-1 list-disc list-inside">
                    {getSafeArray('staticContent:manual.sections.bookingLink.tip.items').map((item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              </section>

              {/* 設定 */}
              <section id="settings" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {t('staticContent:manual.sections.settings.title')}
                </h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('staticContent:manual.sections.settings.description')}
                </p>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                  {t('staticContent:manual.sections.settings.configurations.title')}
                </h4>
                <div className="space-y-4 mb-6">
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h5 className="font-semibold mb-2 text-gray-900 dark:text-white">
                      {t('staticContent:manual.sections.settings.configurations.basic.title')}
                    </h5>
                    <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      {getSafeArray('staticContent:manual.sections.settings.configurations.basic.items').map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h5 className="font-semibold mb-2 text-gray-900 dark:text-white">
                      {t('staticContent:manual.sections.settings.configurations.hours.title')}
                    </h5>
                    <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      {getSafeArray('staticContent:manual.sections.settings.configurations.hours.items').map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h5 className="font-semibold mb-2 text-gray-900 dark:text-white">
                      {t('staticContent:manual.sections.settings.configurations.slots.title')}
                    </h5>
                    <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      {getSafeArray('staticContent:manual.sections.settings.configurations.slots.items').map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h5 className="font-semibold mb-2 text-gray-900 dark:text-white">
                      {t('staticContent:manual.sections.settings.configurations.capacity.title')}
                    </h5>
                    <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      {getSafeArray('staticContent:manual.sections.settings.configurations.capacity.items').map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                    {t('staticContent:manual.sections.settings.tip.title')}
                  </h4>
                  <p className="text-blue-800 dark:text-blue-200 text-sm">
                    {t('staticContent:manual.sections.settings.tip.content')}
                  </p>
                </div>
              </section>

              {/* 資料管理 */}
              <section id="dataManagement" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {t('staticContent:manual.sections.dataManagement.title')}
                </h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('staticContent:manual.sections.dataManagement.description')}
                </p>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
                  {t('staticContent:manual.sections.dataManagement.features.title')}
                </h4>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
                  {getSafeArray('staticContent:manual.sections.dataManagement.features.items').map((item: any, index: number) => (
                    <li key={index}>
                      <strong>{item.title}</strong>{item.description}
                    </li>
                  ))}
                </ul>
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <h4 className="font-semibold text-red-900 dark:text-red-100 mb-2">
                    {t('staticContent:manual.sections.dataManagement.warning.title')}
                  </h4>
                  <p className="text-red-800 dark:text-red-200 text-sm">
                    {t('staticContent:manual.sections.dataManagement.warning.content')}
                  </p>
                </div>
              </section>

          </div>
        </div>
    </div>
  );
};

export default ManualPage;
